import React from 'react';
import { Card, ToggleContainer, Section, Button, ToastContext } from '@weezevent/nacre';
import OrganizationSettings from '../../models/organizationSettings';
import { useForm } from '@weezevent/weezjs-core';
import { useTranslation } from 'react-i18next';
import Style from '../../styles/partners/partner-settings.module.css';

export default function AttendeesManagement({ organization, attendees_management = {}, sync = () => {} }) {
    const { t } = useTranslation();
    const toast = React.useContext(ToastContext);

    const defaultOptions = {
        ticket_edition_conditions: {
            downloaded_tickets: true,
            sent_tickets: true,
            scanned_tickets: false
        },
        ticket_deletion_conditions: {
            downloaded_tickets: true,
            sent_tickets: true,
            scanned_tickets: false
        }
    };

    const options = React.useMemo(() => {
        return { ...defaultOptions, ...attendees_management };
    }, [attendees_management]);
    const [formOptions, setFormOptions, setObjectOptions] = useForm({ ...options });

    const togglesDefaultValue = React.useMemo(
        () =>
            Object.keys(options).reduce((final, key) => {
                final[key] = Object.values(options[key]).some(o => o === true);
                return final;
            }, {}),
        [options]
    );
    const [togglesState, setTogglesState, setTogglesObject] = useForm({ ...togglesDefaultValue });

    const setAllOptions = React.useCallback(
        (action, state) => {
            let newObject = {};
            if (!state)
                Object.keys(formOptions[action]).forEach(key => {
                    newObject[key] = false;
                });
            else newObject = { ...options[action] };
            setFormOptions(action, newObject);
        },
        [formOptions, setFormOptions, options]
    );

    const setOption = React.useCallback(
        (action, option, value) => {
            let newObject = { ...formOptions[action] };
            newObject[option] = value;
            setFormOptions(action, newObject);
        },
        [formOptions, setFormOptions]
    );

    const deepCompare = (obj1, obj2) => JSON.stringify(obj1) === JSON.stringify(obj2);

    const handleSubmit = React.useCallback(() => {
        let organizationSettings = new OrganizationSettings();
        organizationSettings.attendees_management = formOptions;
        organizationSettings
            .save({
                organizationId: organization.pk
            })
            .then(() => {
                toast.success(t('partners.attendees_management.success'));
                sync();
            })
            .catch(e => {
                toast.error(t('partners.attendees_management.error'));
            });
    }, [formOptions, sync]);
    const handleRestore = React.useCallback(() => {
        setObjectOptions(options);
        setTogglesObject(togglesDefaultValue);
    }, [options, setObjectOptions, togglesDefaultValue, setTogglesObject]);

    return (
        <Card>
            <Card.Header>
                <Card.Title title={t('partners.attendees_management.title')} />
            </Card.Header>
            <Card.Content>
                {Object.entries(formOptions)?.map(([action, objectOptions], index) => (
                    <ToggleContainer
                        title={t(`partners.attendees_management.${action.replace('ticket_', '').replace('_conditions', '')}.title`)}
                        subtitle={t(`partners.attendees_management.${action.replace('ticket_', '').replace('_conditions', '')}.description`)}
                        key={index}
                        toggle={togglesState[action]}
                        onToggle={toggle => {
                            setTogglesState(action, toggle);
                            setAllOptions(action, toggle);
                        }}
                    >
                        <Section>
                            {Object.keys(objectOptions)?.map((option, id) => (
                                <ToggleContainer
                                    key={id}
                                    title={t(`partners.attendees_management.${option.split('_')[0]}`)}
                                    toggle={formOptions[action][option]}
                                    onToggle={toggle => setOption(action, option, toggle)}
                                />
                            ))}
                        </Section>
                    </ToggleContainer>
                ))}
                {!deepCompare(formOptions, options) && (
                    <div>
                        <Button primary className={Style['submit-button']} onClick={handleSubmit}>
                            {t('ticket.common.action.save')}
                        </Button>
                        <Button onClick={handleRestore}>{t('ticket.common.action.restore')}</Button>
                    </div>
                )}
            </Card.Content>
        </Card>
    );
}
